import { Component, OnInit, OnDestroy, NgZone, ChangeDetectorRef, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Sidenavaction } from '../../../../assets/js/application/sidenavaction';
import { BusinessEntityService } from '../../../core/services/business.service';
import BusinessEntity, {
  Address,
  BrandingAttachments, BusinessCategory, BusinessType, ProviderID
} from '../../../core/models/business.model';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import PersonsModel from '../../../core/models/persons.model';
import { PersonsService } from '../../../core/services/persons.service';
import * as moment from 'moment';
import * as _ from 'lodash';
import BusinessCategoryModel from '../../../core/models/business-category.model';
import { BusinessCategoryService } from '../../../core/services/business-category.service';
import { BusinessTypeService } from '../../../core/services/business-type.service';
import BusinessTypeModel from '../../../core/models/business-type.model';
import CustomerLeadModel from '../../../core/models/customer-lead.model';
import { CustomerLeadService } from '../../../core/services/customer-lead.service';
import { ToastrService } from 'ngx-toastr';
import { CommonMethodsService } from 'src/app/core/services/common-methods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/core/services/previous-route.service';
import { CountriesAPIService } from 'src/app/core/services/countries.service';
import CountriesModel from 'src/app/core/models/countries.model';
import { StateModel } from 'src/app/core/models/json.model';
import { StatesAPIService } from 'src/app/core/services/states.service';
import { BatchLoader } from 'src/app/core/utilities/batch-loader.util';

declare var $: any;

/**
 * @component BusinessEntityComponent
 * @selector app-business-entity
 * @implements {OnInit, OnDestroy}
 *
 * @description
 * The `BusinessEntityComponent` manages the lifecycle of Business Entities within the application.
 * It serves as the main interface for creating, updating, viewing, and managing business profiles
 * such as Farms, Providers, SMEs, and Buyers.
 *
 * ## Key Responsibilities:
 * 1. **Business Registration**: Handles the creation of new business entities with detailed information including address, contact details, and business type.
 * 2. **Business Updates**: Allows editing of existing business profiles.
 * 3. **Data Management**: Fetches and displays lists of business entities, filtering by type and category.
 * 4. **Location Management**: Integrates with Country, State, and Village APIs to provide cascading location selection with search and pagination.
 * 5. **File Handling**: Supports uploading branding attachments (logos) to S3.
 * 6. **Customer Leads**: Integrates with Customer Lead service to convert registrations into leads.
 * 7. **Validation**: Implements comprehensive form validation for fields like mobile numbers (country-specific), emails, and required fields.
 *
 * ## Features:
 * - **Dynamic Form**: Reactive form with validation that adapts based on selected country (e.g., mobile number patterns).
 * - **Infinite Scroll Dropdowns**: Custom implementation for loading large datasets (villages) in chunks.
 * - **Role-Based Access**: Adapts view and functionality based on logged-in user roles (Admin, Farmer, etc.).
 * - **Date Handling**: Custom date pickers for validity periods.
 *
 * ## LocalStorage Dependencies:
 * - `loginPersonType`: User role.
 * - `loginBusinessCategory`: Business context.
 * - `businessID`: ID of the logged-in user's business.
 * - `LoginUserName`: Name of the logged-in user.
 *
 * @service BusinessEntityService - For CRUD operations on business entities.
 * @service PersonsService - For managing persons associated with businesses.
 * @service BusinessTypeService - For fetching business types.
 * @service BusinessCategoryService - For fetching business categories.
 * @service CustomerLeadService - For managing customer leads.
 * @service CountriesAPIService - For fetching country data.
 * @service StatesAPIService - For fetching state and village data.
 * @service CommonMethodsService - For utility functions and error handling.
 */
@Component({
  selector: 'app-businesses',
  templateUrl: './businesses.component.html',
  styleUrls: ['./businesses.component.scss']
})
export class BusinessesComponent implements OnInit, OnDestroy {
  /** @type {Array<BusinessEntity>} List of all business entities. */
  public businessEntities: Array<BusinessEntity>;

  /** @type {BusinessEntity} Current business entity model being created or edited. */
  public businessEntity: BusinessEntity;

  /** @type {Array<CustomerLeadModel>} List of customer leads. */
  public customerLeads: Array<CustomerLeadModel>;

  /** @type {CustomerLeadModel} Current customer lead model. */
  public customerLead: CustomerLeadModel;

  /** @type {Array<BusinessEntity>} List of provider businesses. */
  public providerBusiness: Array<BusinessEntity>;

  /** @type {Address} Address model for the business. */
  public address: Address;

  /** @type {BusinessType} Business type model. */
  public businessType: BusinessType;

  /** @type {BusinessCategory} Business category model. */
  public businessCategory: BusinessCategory;

  /** @type {BrandingAttachments} Branding attachment model for logos. */
  public brandindAttachments: BrandingAttachments;

  /** @type {ProviderID} Provider ID model. */
  public providerId: ProviderID;

  /** @type {PersonsModel} Selected person model. */
  public person: PersonsModel;

  /** @type {Array<PersonsModel>} List of persons associated with businesses. */
  public persons: Array<PersonsModel>;

  /** @type {Array<PersonsModel>} List of all persons fetched. */
  public persons1: Array<PersonsModel>;

  /** @type {Array<BusinessCategoryModel>} List of available business categories. */
  public businessCategories: Array<BusinessCategoryModel>;

  /** @type {Array<BusinessTypeModel>} List of available business types. */
  public businessTypes: Array<BusinessTypeModel>;

  /** @type {boolean} Page loading state flag. */
  public pgLoad = false;

  /** @type {string} Search keyword for filtering lists. */
  public search: string;

  /** @type {FormGroup} Reactive form for business registration. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag indicating if form has been submitted. */
  public submitted: boolean;

  /** @type {boolean} Flag indicating if a file is selected for upload. */
  public isFileSelected: boolean;

  /** @type {string | ArrayBuffer} Base64 representation of selected file. */
  public selectedbase64file: string | ArrayBuffer;

  /** @type {File | any} Selected image file object. */
  public imageFile: File | any;

  /** @type {boolean} Flag indicating if file selection was cleared. */
  public isCleared: boolean;

  /** @type {boolean} Flag to show submit button. */
  public submitButton: boolean;

  /** @type {boolean} Flag to show update button. */
  public updateButton: boolean;

  /** @type {string} ID of the person associated with the business. */
  public personId: string;

  /** @type {string} Name of the provider business. */
  public providerBusinessName: string;

  /** @type {string} Generic item string. */
  public item: string;

  /** @type {string} Keyword for search. */
  public keyword: string;

  /** @type {Array<BusinessEntity>} List of pincodes (unused). */
  public pinCodes: Array<BusinessEntity>;

  /** @type {Array<BusinessTypeModel>} Duplicate list of business types. */
  public duplicatebusinessTypes: Array<BusinessTypeModel>;

  /** @type {any} Parameter indicating if user is admin. */
  public paramBusiness: any;

  /** @type {boolean} Loader flag. */
  public loader: boolean;

  /** @type {any} Filtered array of businesses. */
  public filteredBusinessessArray: any;

  /** @type {any} Duplicate filtered array of businesses. */
  public dupFilteredBusinessessArray: any;

  /** @type {Array<BusinessTypeModel>} Filtered list of business types. */
  public businessTypesFiltered: Array<BusinessTypeModel>;

  /** @type {boolean} Flag to prevent double clicks on creation. */
  public businessCreationDoubleClick: boolean;

  /** @type {string} Name of the uploaded file. */
  public fileName: string;

  /** @type {string} Path of the uploaded file. */
  public filePath: string;

  /** @type {boolean} Flag to clear search input. */
  public clearSearchInput: boolean;

  /** @type {any[]} Filtered options for dropdowns. */
  public filteredOptions: any[] = [];

  /** @type {string} Processed farm name. */
  public finalFarmName: string;

  /** @type {Array<any>} List of countries. */
  public countries = new Array();

  /** @type {Array<StateModel>} List of states for selected country. */
  public states: Array<StateModel>;

  /** @type {Array<any>} List of villages for selected state. */
  public villages: Array<any>;

  /** @type {boolean} Flag for country dropdown state. */
  public isCountryDropdown = false;

  /** @type {boolean} Flag for state dropdown state. */
  public isStateDropdown = false;

  /** @type {boolean} Flag for village dropdown state. */
  public isVillageDropdown = false;

  /** @type {any[]} Filtered list of countries for autocomplete. */
  public filteredCountriesList: any[] = [];

  /** @type {any[]} Filtered list of states for autocomplete. */
  public filteredStatesList: any[] = [];

  /** @type {any[]} Filtered list of villages for autocomplete. */
  public filteredVillagesList: any[] = [];

  /** @type {any[]} Limited list of villages for performance. */
  public limitedVillagesList: any[] = [];

  /** @type {string} Search string for country. */
  public countrySearch: string = 'Enter Country name';

  /** @type {string} Search string for state. */
  public stateSearch: string = 'Enter State name';

  /** @type {string} Search string for village. */
  public villageSearch: string = 'Enter Village name';

  /** @type {number} Count of form fields. */
  public formFieldCount: number = 0;

  /** @type {boolean} Flag to show/hide the form. */
  public showForm: boolean = false;

  /** @type {boolean} Flag indicating if country selection changed. */
  public isCountryChanged: boolean;

  /** @type {boolean} Flag indicating if state selection changed. */
  public isStateChanged: boolean;

  /** @type {{ startDate: moment.Moment, endDate: moment.Moment }} Selected date range. */
  public selected: { startDate: moment.Moment, endDate: moment.Moment };

  /**
  * Flag to control the visibility of the list loader spinner during infinite scroll.
  * @type {boolean}
  * @default false
  */
  public listLoader = false;

  /**
   * Constructor for BusinessEntityComponent.
   * Initializes services and reactive forms.
   *
   * @param {ToastrService} toasterService - Service for toast notifications.
   * @param {CustomerLeadService} customerLeadService - Service for customer lead operations.
   * @param {BusinessTypeService} businessTypeService - Service for business type operations.
   * @param {BusinessCategoryService} businessCategoryService - Service for business category operations.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {PersonsService} personsService - Service for person operations.
   * @param {ActivatedRoute} route - Service for retrieving route parameters.
   * @param {Sidenavaction} sidenavaction - Service for side navigation.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {Router} router - Angular Router.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   * @param {CountriesAPIService} countriesAPIService - Service for fetching countries.
   * @param {StatesAPIService} statesAPIService - Service for fetching states and villages.
   * @param {NgZone} ngZone - Angular Zone service for change detection.
   * @param {ChangeDetectorRef} cdr - Change detector reference.
   */
  constructor(public toasterService: ToastrService, public customerLeadService: CustomerLeadService, public businessTypeService: BusinessTypeService, public businessCategoryService: BusinessCategoryService, public businessEntityService: BusinessEntityService, public personsService: PersonsService, public route: ActivatedRoute, public sidenavaction: Sidenavaction, public formBuilder: FormBuilder, public commonMethodsService: CommonMethodsService,
    public router: Router, private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService, public countriesAPIService: CountriesAPIService, public statesAPIService: StatesAPIService,
    private ngZone: NgZone, private cdr: ChangeDetectorRef) {
    this.businessEntity = new BusinessEntity();
    this.address = new Address();
    this.businessType = new BusinessType();
    this.businessCategory = new BusinessCategory();
    this.brandindAttachments = new BrandingAttachments();
    this.providerId = new ProviderID();
    this.customerLead = new CustomerLeadModel();
    // Business Details registraion validation
    this.registerForm = this.formBuilder.group({
      businessName: ['', [Validators.required,
      Validators.minLength(5),
      Validators.maxLength(30),
      Validators.pattern('^[a-zA-Z ]+$'),
      ]],
      businessCode: [null],
      businessType: ['', Validators.required],
      email: ['', [Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-z]{2,6}$')
      ]],
      mobilenumber: ['', Validators.required],
      inputhouseNo: ['', [
        Validators.pattern('^[ A-Za-z0-9 ,./#-]*$'),
        Validators.minLength(3),
        Validators.maxLength(30)
      ]],
      inputstreet: ['', [
        Validators.minLength(3),
        Validators.maxLength(30),
        Validators.pattern('^[a-zA-Z\\s\\W]*$')
      ]],
      inputpincode: ['', [
        Validators.minLength(6),
        Validators.maxLength(6),
        Validators.pattern('^[0-9]{6,6}$'),
        Validators.required
      ]],
      btype: [null],
      logo: [null],
      providers: [null],
      enablefp: [null],
      personId: [null],
      caaid: ['', [Validators.maxLength(50)]],
      mpedaid: ['', [Validators.maxLength(50)]],
      validityField: this.formBuilder.group({
        start: ['', Validators.required],
        end: ['', Validators.required]
      }),
      countryName: ['', [Validators.required]],
      stateName: ['', [Validators.required]],
      villageName: ['', [Validators.required]],
      dialCode: [null],
      category: ['', [Validators.required]],
    });

    // Calculate field count after form is built
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets previous route.
   * - Initializes form state and flags.
   * - Fetches reference data (Countries, Business Types, Categories, Persons).
   * - Loads business entities and filters them by type.
   * - Sets up side navigation.
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.clearSearchInput = false;
      this.isFiltering = false;
      this.businessCreationDoubleClick = false;
      this.keyword = 'name';
      this.closeForm();
      this.search = '';
      this.sidenavaction.x_navigation();
      this.submitButton = false;
      this.updateButton = false;
      this.loader = false;
      this.pgLoad = false;
      this.imageFile = undefined;
      this.isFileSelected = false;
      this.selectedbase64file = undefined;
      this.fileName = undefined;
      this.item = undefined;
      this.countries = [];
      this.businessType.name = '';
      this.businessCategory.name = '';
      this.businessEntity.startDate = '';
      this.businessEntity = new BusinessEntity();
      this.customerLead = new CustomerLeadModel();
      this.brandindAttachments = new BrandingAttachments();
      this.businessType = new BusinessType();
      this.businessCategory = new BusinessCategory();
      this.providerId = new ProviderID();
      this.filteredBusinessessArray = new Array();
      this.dupFilteredBusinessessArray = new Array();
      this.businessTypesFiltered = new Array();
      this.personId = '';
      // Promise All
      this.countries = await this.countriesAPIService.getAllCountriesList();
      if (this.countries) {
        this.countrySearch = 'India';
        this.address.country = 'India';
        this.address.countryCode = 'In';
        const indianCountryIndexPosition = this.countries.findIndex((country) => country.name == 'India');
        const countryObj = this.countries.find((country) => country.name == 'India');
        if (indianCountryIndexPosition != -1) {
          const objectToMove = this.countries.splice(indianCountryIndexPosition, 1);
          this.countries.unshift(objectToMove[0]);
          this.filteredCountriesList = this.countries;
          this.mobileNumberValidation(countryObj);
          this.getDialCode();
          this.getStates();
        }
      }
      $('#imageUpload').val('');
      this.businessEntities = (await this.businessEntityService.getAllBusiness())?.filter(b => b.businessType?.[0]?.businessTypeID == '618a17b80f339c001e991b03') || [];
      this.dupFilteredBusinessessArray = this.businessEntities;
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader?.reset();     // Clear state
      await this.batchLoader?.loadNextBatch(true); // Load first batch
      this.businessCategories = (await this.businessCategoryService.getAllBusinessCategories()).filter(c => c.id == '61890be727c16c001eb0d9a6');
      this.loader = true;
      this.pgLoad = true;
      this.customerLeads = await this.customerLeadService.getAllCustomerLeads();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
    * Host listener for window scroll events.
    * Triggers the check for loading the next batch of data when scrolling near the bottom.
    *
    * @method onScroll
    * @returns {void}
    */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  /**
   * Timeout reference for scroll event debouncing.
   * @type {any}
   * @private
   */
  private scrollTimeout: any;

  /**
    * Utility for handling batch loading of data for infinite scroll.
    * @type {BatchLoader<any>}
    * @private
    */
  private batchLoader: BatchLoader<any>;

  /**
* Flag indicating if a search filter is currently active.
* @type {boolean}
* @default false
*/
  public isFiltering: boolean = false;

  /**
   * Checks scroll position and loads next batch if near bottom.
   * Debounced to prevent excessive calls.
   *
   * @method checkScrollAndLoad
   * @private
   * @returns {void}
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * Initializes the BatchLoader instance.
   * Configures the source list (filtered or full) and the callback to update the displayed list.
   *
   * @method initializeBatchLoader
   * @private
   * @returns {void}
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.dupFilteredBusinessessArray : this.businessEntities,
      (updatedList) => {
        this.dupFilteredBusinessessArray = updatedList;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method mobileNumberValidation
   * @description Sets validation patterns for mobile number and pincode based on the selected country.
   * @param {any} country - The selected country object.
   * @side-effect Updates validators for `mobilenumber` and `inputpincode` controls.
   */
  mobileNumberValidation(country: any) {
    switch (country.name) {
      case 'Spain':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{9,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Indonesia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{5,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Nigeria':
      case 'Vietnam':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{7,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6,6}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Malaysia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{7,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Australia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{5,15}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(4), Validators.maxLength(4), Validators.pattern('^[0-9]{4,4}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Ecuador':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{8,8}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6,6}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Saudi Arabia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{8,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      default:
        this.registerForm.get('mobilenumber').setValidators([Validators.minLength(10), Validators.maxLength(10), Validators.pattern('^[0-9]{10,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6,6}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
    }
  }

  /**
   * @method getDialCode
   * @description Retrieves and sets the dialing code for the selected country.
   * @side-effect Updates `businessEntity.dialingCode`.
   */
  public getDialCode() {
    let item = this.countries.find(code => code.name == this.countrySearch);
    this.businessEntity.dialingCode = String(item.dialingCode[0]);
  }

  /* Country Selection Field Start */
  /**
   * @method filterCountries
   * @description Filters the list of countries based on user input in the country search field.
   * @side-effect Updates `filteredCountriesList`.
   */
  public filterCountries(): void {
    const value = (this.registerForm.get('countryName')!.value || '') as CountriesModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredCountriesList = this.countries.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method coutrySelection
   * @description Handles country selection from the dropdown.
   * Updates form values, triggers validation updates, and fetches states.
   * @param {any} country - The selected country object.
   */
  public coutrySelection(country: any): void {
    this.registerForm.get('countryName')!.setValue(country.name);
    this.isCountryDropdown = false;
    this.countrySearch = country.name;
    this.address.country = country.name;
    this.isCountryChanged = true;
    this.getDialCode();
    this.registerForm.get('mobilenumber').clearValidators();
    this.registerForm.get('inputpincode').clearValidators();
    this.getStates();
    switch (country.name) {
      case 'Spain':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{9,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Indonesia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{5,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Nigeria':
      case 'Vietnam':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{7,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6,6}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Malaysia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{7,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Australia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{5,15}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(4), Validators.maxLength(4), Validators.pattern('^[0-9]{4,4}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Ecuador':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{8,8}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6,6}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      case 'Saudi Arabia':
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{8,9}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(5), Validators.maxLength(5), Validators.pattern('^[0-9]{5,5}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
      default:
        this.registerForm.get('mobilenumber').setValidators([Validators.pattern('^[0-9]{10,10}$'), Validators.required]);
        this.registerForm.get('mobilenumber').updateValueAndValidity();
        this.registerForm.get('inputpincode').setValidators([Validators.minLength(6), Validators.maxLength(6), Validators.pattern('^[0-9]{6,6}$'), Validators.required]);
        this.registerForm.get('inputpincode').updateValueAndValidity();
        break;
    }
  }
  /* Country Selection Field end */

  /* State Selection Field Start */
  /**
   * @method filterStates
   * @description Filters the list of states based on user input in the state search field.
   * @side-effect Updates `filteredStatesList`.
   */
  public filterStates(): void {
    const value = (this.registerForm.get('stateName')!.value || '') as StateModel | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.name.toLowerCase();

    this.filteredStatesList = this.states.filter(o =>
      o.name.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method stateSelection
   * @description Handles state selection from the dropdown.
   * Updates form values and triggers village fetching.
   * @param {any} state - The selected state object.
   */
  public stateSelection(state: any): void {
    this.registerForm.get('stateName')!.setValue(state.name);
    this.isStateDropdown = false;
    this.stateSearch = state.name;
    this.isStateChanged = true;
    this.address.state = state.name;
    this.getVillages(state);
  }
  /* State Selection Field end */

  /**
   * @method
   * @method getStates
   * @description Retrieves and populates the list of states based on the selected country.
   * Clears previous state/village selections if in edit mode or country changed.
   */
  public getStates(): void {
    this.states = [];
    this.villages = [];
    this.filteredStatesList = [];
    this.filteredVillagesList = [];
    if (this.submitButton || (this.updateButton && this.isCountryChanged)) {
      this.registerForm.get('stateName')!.setValue('');
      this.registerForm.get('villageName')!.setValue('');
    }
    const selectedCountryObject = this.countries.find((country: CountriesModel) => country.name == this.countrySearch);
    this.states = selectedCountryObject.states;
    this.filteredStatesList = this.states;

  }

  /**
    * @method getVillages
    * @async
    * @description Retrieves and populates the list of villages based on the selected state.
    * Handles loading states and error flags.
    */
  public isLoadingVillages = false;
  public isLoadingMore = false;
  public villagesLoadError = false;
  public visibleVillages: any[] = [];
  public CHUNK_SIZE = 50;
  private currentIndex = 0;
  private searchText = '';
  private isLoadTriggered = false;
  public noDataFound = false;

  public async getVillages(state: any) {
    this.resetVillages();
    this.isLoadingVillages = true;
    this.villagesLoadError = false;
    this.noDataFound = false;

    try {
      const response = await this.statesAPIService.getVillagesByStates(state.name);
      if (response?.[0]?.city_town_village?.length) {
        this.villages = response[0].city_town_village;
        this.loadNextChunk();
      } else {
        this.villagesLoadError = true;
      }
    } catch (err) {
      this.villagesLoadError = true;
    } finally {
      this.isLoadingVillages = false;
    }
  }

  /**
   * @method loadNextChunk
   * @private
   * @description Loads the next chunk of villages for infinite scroll or initial load.
   */
  private loadNextChunk() {
    const source = this.searchText
      ? this.villages.filter(v =>
        v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
      )
      : this.villages;

    if (source.length === 0) {
      this.noDataFound = true;
      return;
    }

    this.noDataFound = false; // reset when data exists
    const nextSet = source.slice(this.currentIndex, this.currentIndex + this.CHUNK_SIZE);
    this.visibleVillages = [...this.visibleVillages, ...nextSet];
    this.currentIndex += this.CHUNK_SIZE;
  }

  /**
   * @method onVillageScroll
   * @description Handles scroll event on the village dropdown to trigger loading more items.
   * Uses `ngZone` for performance optimization.
   */
  public onVillageScroll() {
    if (this.isLoadTriggered || this.isLoadingMore) return;

    const remaining = this.getFilteredList()?.length - this.visibleVillages.length;
    if (remaining > 0) {
      this.isLoadTriggered = true;
      this.isLoadingMore = true;

      setTimeout(() => {
        this.ngZone.run(() => {
          this.loadNextChunk();
          this.isLoadingMore = false;
          this.isLoadTriggered = false;
          this.cdr.detectChanges();
        });
      }, 300);
    }
  }

  /**
   * @method filterVillages
   * @description Filters the village list based on user input.
   * Resets the visible list and reloads chunks.
   */
  public filterVillages(event: any) {
    this.searchText = event.target.value;
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.loadNextChunk();
  }

  /**
   * @method getFilteredList
   * @private
   * @description Helper to get the full filtered list of villages based on search text.
   */
  private getFilteredList() {
    return this.searchText
      ? this.villages.filter(v =>
        v['city_town_village_name'].toLowerCase().includes(this.searchText.toLowerCase())
      )
      : this.villages;
  }

  /**
   * @method resetVillages
   * @private
   * @description Resets village data structures and flags.
   */
  private resetVillages() {
    this.villages = [];
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
    this.isLoadTriggered = false;
    this.noDataFound = false;
    this.registerForm.get('villageName')?.setValue('');
  }

  /**
   * @method onVillageFocus
   * @description Handles focus event on village input to load initial data if needed.
   */
  public onVillageFocus() {
    if (!this.searchText) {
      // If no search text, just reload first chunk
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    } else {
      // If search exists, reload search result
      this.visibleVillages = [];
      this.currentIndex = 0;
      this.loadNextChunk();
    }
  }

  /**
   * @method onVillageBlur
   * @description Handles blur event on village input to clear invalid selections.
   */
  public onVillageBlur() {
    if (this.noDataFound || this.visibleVillages.length == 0) {
      this.clearVillageField();
    }
  }

  /**
   * @method clearVillageField
   * @private
   * @description Clears the village input field and resets state.
   */
  private clearVillageField() {
    this.registerForm.get('villageName')?.setValue('');
    this.visibleVillages = [];
    this.currentIndex = 0;
    this.searchText = '';
  }

  /**
   * @method villageSelection
   * @description Handles village selection from the dropdown.
   * Updates address fields (city, landmark, district) based on selected village string.
   * @param {any} village - The selected village object.
   */
  public villageSelection(village: any): void {
    this.registerForm.get('villageName')!.setValue(village.city_town_village_name);
    this.isVillageDropdown = false;
    this.villageSearch = village.city_town_village_name;
    if (this.villageSearch) {
      this.address.city = village.city_town_village_name.toLowerCase().includes(',') ? village.city_town_village_name.split(',')[0] : village.city_town_village_name;
      this.address.landMark = village.city_town_village_name.toLowerCase().includes(',') ? village.city_town_village_name.split(',')[1] : village.city_town_village_name;
      this.address.district = village.city_town_village_name.toLowerCase().includes(',') ? village.city_town_village_name.split(',')[2] : village.city_town_village_name;
    }
  }
  /* Village Selection Field end */

  /**
   * @method onDateChange
   * @description Updates the business entity valid through date when form value changes.
   */
  public onDateChange() {
    const { validThrough } = this.registerForm.value;
    this.businessEntity.validThrough = moment(validThrough).format('YYYY-MM-DD HH:mm:ss');
  }

  /**
   * @lifecycle ngOnDestroy
   * @description
   * Lifecycle hook that is called when the component is destroyed.
   * Cancels all ongoing HTTP requests to prevent memory leaks.
   * @side-effect Cancels HTTP requests for BusinessEntityService, PersonsService, BusinessCategoryService,
   * BusinessTypeService, and CustomerLeadService.
   */
  public ngOnDestroy(): void {
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.personsService.cancelOnGoingHttpRequests();
    this.businessCategoryService.cancelOnGoingHttpRequests();
    this.businessTypeService.cancelOnGoingHttpRequests();
    this.customerLeadService.cancelOnGoingHttpRequests();
  }

  /**
   * @method getBusinessTypes
   * @async
   * @description Fetches business types for a specific category.
   * @param {string} businessCategoryID - The ID of the business category.
   */
  public async getBusinessTypes(businessCategoryID: string) {
    try {
      this.businessType.businessTypeID = undefined;
      this.businessTypes = (await this.businessTypeService.getBusinessTypesByBusinessCategory(businessCategoryID)).filter(bt => bt.id == '618a17b80f339c001e991b03');
      this.setSingleValueForBusinessType();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method setSingleValueForBusinessType
   * @description Auto-selects the business type if only one option is available.
   */
  public setSingleValueForBusinessType() {
    this.businessType.businessTypeID = undefined;
    if (this.businessTypes && this.businessTypes.length == 1) {
      this.businessType.businessTypeID = this.businessTypes[0].id; // model-binding key
    }
  }

  /**
   * Filters the item type list based on the search query.
   *
   * - Filters by name (case-insensitive).
   * - Updates `filteredItemTypes`.
   * - Resets the batch loader to display filtered results.
   *
   * @async
   * @method applySearch
   * @returns {Promise<void>}
   * @side-effect Updates `filteredItemTypes`, `itemTypesList`, `noList`, `isFiltering`.
   */
  public async searchBusiness(): Promise<void> {
    this.dupFilteredBusinessessArray = [];
    const query = this.search?.replace(/[-_]|\s/g, "").trim().toLowerCase() || '';
    this.isFiltering = query.length > 0;

    if (this.isFiltering) {
      this.dupFilteredBusinessessArray = this.businessEntities.filter(business =>
        business.businessName?.replace(/[-_]|\s/g, "").toLowerCase().startsWith(query) ||
        business.businessType?.[0]?.name?.replace(/[-_]|\s/g, "").toLowerCase().startsWith(query) ||
        business.mobileNumber?.replace(/[-_]|\s/g, "").toLowerCase().startsWith(query) ||
        business.email?.replace(/[-_]|\s/g, "").toLowerCase().startsWith(query)
      );
    } else {
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      this.batchLoader?.loadNextBatch(true);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the template.
   * @returns {AbstractControl} Form controls.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method getBusinessCode
   * @async
   * @description Fetches business code suggestions based on the business name.
   */
  public async getBusinessCode() {
    if (this.businessEntity.businessName != '' && this.businessEntity.businessName != undefined && this.businessEntity.businessName.length > 5) {
      try {
        const businessCodes = await this.businessEntityService.getBusinessCodeSuggestions(this.processFarmName(this.businessEntity.businessName), 'noCode');
        if (businessCodes && businessCodes['suggestions'].length > 0) {
          this.businessEntity.businessCode = businessCodes['suggestions'][0];
        }
      } catch (error) {
      }
    }
  }

  /**
   * @method onBusinessChange
   * @description Clears business code if business name is empty.
   */
  public onBusinessChange() {
    if (this.businessEntity.businessName == '' || this.businessEntity.businessName == undefined) {
      this.businessEntity.businessCode = '';
    }
  }

  /**
   * @method processFarmName
   * @description Formats the farm name to CamelCase for code generation.
   * @param {string} farmName - The raw farm name.
   */
  public processFarmName(farmName: string) {
    let trimmedFarmName = farmName.trim();
    if (trimmedFarmName.includes(' ')) {
      this.finalFarmName = trimmedFarmName
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      return this.finalFarmName;
    } else {
      return trimmedFarmName;
    }
  }

  /**
   * @method addToCustomerLead
   * @async
   * @description Creates a customer lead from the business registration data.
   * Validates mobile number uniqueness before creation.
   * Calls `addBusinessDetailsSubmit` upon success.
   */
  public async addToCustomerLead() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    // stop here if form is invalid // need to check with anusha
    if (this.registerForm.invalid) return;

    let businessType = this.businessTypes.find(businesstype => businesstype.id == this.businessType.businessTypeID);
    let customerLead = this.customerLeads.filter(lead => lead.mobileNumber == this.businessEntity.mobileNumber);

    if (customerLead && customerLead.length > 0) {
      this.toasterService.warning('Mobile Number already exist', '', {
        timeOut: 2000
      });
      this.businessCreationDoubleClick = false;
      return false;
    } else {
      try {
        const countryItem = this.countries.find(countryName => countryName && countryName.name == this.countrySearch);
        this.customerLead.countryCode = countryItem.countryCode;
        this.customerLead.lead_culture = {
          id: 1,
          name: 'Shrimp'
        };
        this.customerLead.mobileNumber = this.businessEntity.mobileNumber;
        this.customerLead.dialingCode = this.businessEntity.dialingCode;
        this.customerLead.lead_from = 'Farmer';
        this.customerLead.event = 'Register as farmer';
        this.customerLead.lead_widget = 'bb_web';
        this.customerLead.isActive = true;
        this.customerLead.status = 'Archived';
        this.customerLead.converted = true;
        this.customerLead.lead_reference = 'customerSupport';
        this.customerLead.businessType = [{
          id: businessType.id,
          name: businessType.name
        }];
        this.customerLead.status_date = (moment(new Date()).format('YYYY-MM-DD')).toString();
        this.customerLead.generatedDate = (moment(new Date()).format('YYYY-MM-DD')).toString();
        let customerLeadAdded = await this.customerLeadService.create(this.customerLead);
        if (customerLeadAdded) {
          this.addBusinessDetailsSubmit();
        }
      } catch (error) {
        this.commonMethodsService.userFriendlyAlertMessages(error);
        this.businessCreationDoubleClick = false;
      }
    }
  }

  /**
   * @method addBusinessDetailsSubmit
   * @async
   * @description Submits the new business entity details.
   * Handles file upload for branding if selected.
   */
  public async addBusinessDetailsSubmit() {
    this.businessCreationDoubleClick = true;

    this.businessEntity.validThrough = moment(this.selected?.endDate).format('DD-MMM-YYYY');
    this.businessEntity.startDate = moment(this.selected?.startDate).format('DD-MMM-YYYY');
    this.businessEntity.endDate = moment(this.selected?.endDate).format('DD-MMM-YYYY');
    const type = this.businessTypes.find(btype => btype && btype.id == this.businessType.businessTypeID);
    this.businessType.name = type.name;
    const category = this.businessCategories.find(bCategory => bCategory && bCategory.id == this.businessCategory.businessCategoryID);
    this.businessCategory.name = category.name;
    if (this.providerId.businessID) {
      this.businessEntity.providerID.push(this.providerId);
    }
    if (!this.isFileSelected && this.isCleared) {
      this.businessEntity.branding = false;
      this.businessEntity.branding_attachments = [];
    }
    if (this.personId) {
      this.businessEntity.personId = this.personId;
    }
    this.businessEntity.businessType.push(this.businessType);
    this.businessEntity.businessCategory.push(this.businessCategory);
    this.businessEntity.address[0].houseNo = this.address.houseNo || '';
    this.businessEntity.address[0].street = this.address.street || '';
    this.businessEntity.address[0].city = this.address.city || '';
    this.businessEntity.address[0].pincode = this.address.pincode || '';
    this.businessEntity.address[0].district = this.address.district || '';
    this.businessEntity.address[0].landMark = this.address.landMark || '';
    this.businessEntity.address[0].state = this.address.state || '';
    this.businessEntity.address[0].country = this.address.country || '';
    this.businessEntity.address[0].countryCode = this.address.countryCode || '';
    try {
      let image = this.imageFile;
      let business = this.businessEntity;
      if (this.isFileSelected) {
        this.businessEntityService.uploadFileToS3Bucket(this.imageFile, this.businessEntity.businessName, 'Branding', `${this.imageFile.name}`).subscribe(async (response) => {
          if (response) {
            business.branding = true;
            business.branding_attachments = [
              {
                file_type: image.type.split('/')[1],
                file_path: Object.values(response)[0]
              }
            ];
            let businessAdded = await this.businessEntityService.create(business);
            if (businessAdded) {
              this.toasterService.success('Business created successfully', '', {
                timeOut: 2000
              });
              await this.ngOnInit();
            }
          } else {
            this.toasterService.error('failed to upload image---', '', {
              timeOut: 2000
            });
          }
        });
      } else {
        let businessAdded = await this.businessEntityService.create(this.businessEntity);
        if (businessAdded) {
          this.toasterService.success('Business created successfully', '', {
            timeOut: 2000
          });
          await this.ngOnInit();
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.businessCreationDoubleClick = false;
    }
  }

  /**
   * @method fileupload
   * @description Handles file selection for branding logo upload.
   * @param {any} event - The file input change event.
   */
  public fileupload(event: any) {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file) {
        if (file.size > 2097152) {
          this.toasterService.error('Image size should not exceed 2MB', '', {
            timeOut: 2000
          });
          $('#imageUpload').val('');
        } else {
          this.imageFile = file;
          this.isFileSelected = true;
          this.fileName = file.name;
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => {
            const dataresult = '' + reader.result;
            this.selectedbase64file = dataresult;
          };
          reader.onerror = () => {
          };
        }
      } else {
        this.isFileSelected = false;
      }
    }
  }

  /**
   * @method remove
   * @description Removes the selected branding image.
   */
  public remove() {
    const deleteimage = confirm('Are you sure you want to delete these image?');
    if (deleteimage) {
      this.imageFile = undefined;
      this.isFileSelected = false;
      this.fileName = undefined;
      this.isCleared = true;
      this.selectedbase64file = undefined;
      $('#imageUpload').val('');
    }
  }

  /**
   * @method getBusinessEntityById
   * @async
   * @description Fetches business entity details by ID for editing.
   * Populates the form with existing data including address and branding.
   * @param {string} id - The ID of the business entity.
   */
  public async getBusinessEntityById(id) {
    this.updateButton = true;
    this.submitButton = false;
    this.showForm = true;
    this.isCountryDropdown = false;
    this.isStateDropdown = false;
    this.isVillageDropdown = false;
    this.isCountryChanged = false;
    this.isStateChanged = false;
    this.isFileSelected = false;
    this.providerBusinessName = '';
    this.address = new Address();
    const foundBusiness = this.businessEntities.find(business => business?.id === id);
    this.businessEntity = foundBusiness ? { ...foundBusiness } : new BusinessEntity(); // clone object
    if (this.businessEntity) {
      this.customerLead = this.customerLeads.find(customerlead => customerlead && customerlead.mobileNumber == this.businessEntity.mobileNumber);
      if (this.businessEntity.address.length > 0) {
        this.address = this.businessEntity.address[0];
        const selectedCountryObject = this.countries.find((country: CountriesModel) => country.name == this.address.country);
        const stateObj = selectedCountryObject.states.find((state: StateModel) => state.name == this.address.state);
        this.countrySearch = this.address.country;
        this.registerForm.get('countryName')!.setValue(this.countrySearch);
        this.mobileNumberValidation(selectedCountryObject);
        this.stateSearch = this.address.state;
        this.getStates();
        this.registerForm.get('stateName')!.setValue(this.stateSearch);
        this.villageSearch = this.address.city + ',' + this.address.landMark + ',' + this.address.district;
        this.getVillages(stateObj);
        this.registerForm.get('villageName')!.setValue(this.villageSearch);

      }
      if (this.businessEntity.branding_attachments && this.businessEntity.branding_attachments.length > 0) {
        let filePath = this.businessEntity.branding_attachments[0].file_path;
        const file = this.businessEntityService.downloadFileFromS3Bucket(filePath);
        file.subscribe(fileBlob => {
          const readFile = new FileReader();
          readFile.readAsDataURL(fileBlob);
          readFile.onload = () => {
            this.selectedbase64file = readFile.result;
          }
        });
      }
      this.personId = this.businessEntity && this.businessEntity.person && this.businessEntity.person[0] && this.businessEntity.person[0].id;
      if (this.personId === undefined) {
        this.personId = '';
      }
      this.registerForm.get('validityField').patchValue({
        start: this.businessEntity.startDate ? new Date(this.businessEntity.startDate) : null,
        end: this.businessEntity.validThrough ? new Date(this.businessEntity.validThrough) : null
      })
      this.personId = this.businessEntity.personId;
      if (this.businessEntity.businessType.length > 0) {
        this.businessType.businessTypeID = this.businessEntity.businessType[0].businessTypeID;
      }
      if (this.businessEntity.businessCategory.length > 0) {
        this.businessCategory.businessCategoryID = this.businessEntity.businessCategory[0].businessCategoryID;
      }
      this.businessTypes = await this.businessTypeService.getBusinessTypesByBusinessCategory(this.businessCategory.businessCategoryID);
      if (this.businessEntity.providerID.length > 0) {
        this.providerId.businessID = this.businessEntity.providerID[0].businessID;
        const pBusiness = this.businessEntities.find(business => business && business.id == this.providerId.businessID);
        this.providerBusinessName = pBusiness.businessName;
      }
    }
  }

  /**
   * @method businessUpdate
   * @async
   * @description Updates an existing business entity.
   * Handles file upload if a new branding image is selected.
   */
  public async businessUpdate() {
    this.submitted = true;
    // // stop here if form is invalid
    if (this.registerForm.invalid) {
      return;
    }
    this.submitted = false;
    this.businessCreationDoubleClick = true;
    this.businessEntity.validThrough = moment(this.selected?.endDate).format('DD-MMM-YYYY');
    this.businessEntity.startDate = moment(this.selected?.startDate).format('DD-MMM-YYYY');
    this.businessEntity.endDate = moment(this.selected?.endDate).format('DD-MMM-YYYY');
    const type = this.businessTypes.find(btype => btype && btype.id == this.businessType.businessTypeID);
    this.businessEntity.businessType = [
      {
        businessTypeID: type.id,
        name: type.name
      }
    ];
    const category = this.businessCategories.find(bCategory => bCategory && bCategory.id == this.businessCategory.businessCategoryID);
    this.businessEntity.businessCategory = [
      {
        name: category.name,
        businessCategoryID: category.id
      }
    ];
    if (this.personId) {
      this.businessEntity.personId = this.personId;
    } else {
      this.businessEntity.personId = '';
    }
    const item = this.countries.find(countryName => countryName && countryName.name == this.businessEntity.address[0].country);
    this.businessEntity.address[0].countryCode = String(item.countryCode);
    this.businessEntity.landlineNumber = this.businessEntity.mobileNumber;
    this.businessEntity.address[0].houseNo = this.address.houseNo || '';
    this.businessEntity.address[0].street = this.address.street || '';
    this.businessEntity.address[0].city = this.address.city || '';
    this.businessEntity.address[0].pincode = this.address.pincode || '';
    this.businessEntity.address[0].district = this.address.district || '';
    this.businessEntity.address[0].landMark = this.address.landMark || '';
    this.businessEntity.address[0].state = this.address.state || '';
    this.businessEntity.address[0].country = this.address.country || '';
    this.businessEntity.address[0].countryCode = this.address.countryCode || '';
    this.submitted = false;
    if (!this.isFileSelected && this.isCleared) {
      this.businessEntity.branding = false;
      this.businessEntity.branding_attachments = [];
    }
    try {
      let business = this.businessEntity;
      let customerLead = this.customerLead;
      let image = this.imageFile;
      if (this.isFileSelected) {
        this.businessEntityService.uploadFileToS3Bucket(this.imageFile, business.businessName, 'Branding', `${image.name}`).subscribe(async (response) => {
          if (response) {
            this.filePath = Object.values(response)[0];
            business.branding = true;
            business.branding_attachments = [
              {
                file_type: image.type.split('/')[1],
                file_path: Object.values(response)[0]
              }
            ];
            let businessUpdate = await this.businessEntityService.updateOneByID(business.id, business);
            if (customerLead && customerLead.mobileNumber && customerLead.mobileNumber != business.mobileNumber) {
              const countryItem = this.countries.find(countryName => countryName && countryName.name == this.countrySearch);
              customerLead.countryCode = countryItem.dialingCode[0];
              customerLead.mobileNumber = business.mobileNumber;
              customerLead.businessType = [{
                id: type.id,
                name: type.name
              }];
              await this.customerLeadService.updateOneByID(customerLead.id, customerLead);
            }
            if (businessUpdate) {
              this.toasterService.success('Business updated successfully.', '', {
                timeOut: 2000
              });
              await this.ngOnInit();
            }
          } else {
            this.toasterService.error('failed to update image---', '', {
              timeOut: 2000
            });
          }
        });
      } else {
        this.businessEntity = await this.businessEntityService.updateOneByID(this.businessEntity.id, this.businessEntity);
        if (customerLead && customerLead.mobileNumber && customerLead.mobileNumber != business.mobileNumber) {
          const countryItem = this.countries.find(countryName => countryName && countryName.name == this.countrySearch);
          customerLead.countryCode = countryItem.dialingCode[0];
          customerLead.mobileNumber = business.mobileNumber;
          customerLead.businessType = [{
            id: type.id,
            name: type.name
          }];
          this.customerLead = await this.customerLeadService.updateOneByID(this.customerLead.id, this.customerLead);
        }
        if (this.businessEntity) {
          this.toasterService.success('Business updated successfully.', '', {
            timeOut: 2000
          });
          await this.ngOnInit();
        }
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.businessCreationDoubleClick = false;
    }
  }

  /**
   * @method closeForm
   * @description Resets the form and component state to default.
   */
  public closeForm() {
    this.registerForm.reset();
    this.showForm = false;
    this.submitButton = false;
    this.updateButton = false;
    this.personId = undefined;
    this.businessEntity = new BusinessEntity();
    this.businessType = new BusinessType();
    this.businessCategory = new BusinessCategory();
    this.customerLead = new CustomerLeadModel();
    this.businessEntity.startDate = '';
    this.businessEntity.validThrough = '';
    this.imageFile = undefined;
    this.fileName = undefined;
    this.isFileSelected = false;
    this.selectedbase64file = undefined;
    this.item = undefined;
    this.submitted = false;
    $('#imageUpload').val('');
    this.isCountryDropdown = false;
    this.isStateDropdown = false;
    this.isVillageDropdown = false;
    this.countrySearch = '';
    this.stateSearch = '';
    this.villageSearch = '';
    this.registerForm.get('stateName')!.setValue('');
    this.registerForm.get('villageName')!.setValue('');
  }

  /**
   * @method addAddress
   * @description Adds a new address object to the business entity.
   */
  public addAddress() {
    this.businessEntity.address.push({
      houseNo: '',
      street: '',
      landMark: '',
      state: '',
      district: '',
      city: '',
      pincode: '',
      countryCode: '',
      country: ''
    });
  }

  /**
   * @method removeAddress
   * @description Removes an address object at the specified index.
   * @param {number} i - The index of the address to remove.
   */
  public removeAddress(i: number) {
    this.businessEntity.address.splice(i, 1);
  }

  /**
   * @method openForm
   * @description Opens the form for creating a new business entity.
   */
  public openForm() {
    this.showForm = true;
    this.addAddress();
    this.submitButton = true;
    this.updateButton = false;
    this.registerForm.get('countryName')!.setValue('India');
    this.countrySearch = 'India';
    this.address.country = 'India';
    this.getDialCode();
    this.getStates();
    this.registerForm.get('validThrough')?.patchValue(new Date());
    const { validThrough } = this.registerForm.value;
    this.businessEntity.validThrough = moment(validThrough).format('YYYY-MM-DD HH:mm:ss');

    // Count form fields to toggle layout
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  /**
   * @method navigateToAccountClosureScreen
   * @description Navigates to the account closure screen for a specific business.
   * @param {string} business_id - The ID of the business.
   */
  public navigateToAccountClosureScreen(business_id: string) {
    this.router.navigate(['/home/account-closure/businessID', business_id]);
  }

  /**
   * @method onPickerClosed
   * @description Handles the closing of the date range picker.
   */
  public onPickerClosed(): void {
    const { start, end } = this.registerForm.get('validityField').value;

    if (start && !end) {
      this.registerForm.get('validityField').patchValue({ end: start }, { emitEvent: false });
      this.onDateChanged({ startDate: start, endDate: start });
    } else if (start && end) {
      this.onDateChanged({ startDate: start, endDate: end });
    }
  }

  /**
   * @method onDateChanged
   * @description Updates the selected date range.
   * @param {{ startDate: Date; endDate: Date }} range - The selected date range.
   */
  public onDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.selected = {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate)
    };
  }
}
